/**
 * Keys the pre-Service-Worker build persisted in `localStorage`.
 *
 * `refreshToken` held a real, still-valid ROPC refresh token (written by the old
 * `OpenIDClient` via `setLocalStorageItem`); `accessToken` and `microsoftLogged`
 * were part of the same session bookkeeping.
 */
const LEGACY_AUTH_KEYS = ['refreshToken', 'accessToken', 'microsoftLogged'];

/**
 * Remove the credentials the pre-Service-Worker build left in `localStorage`.
 *
 * Tokens now live in the worker's IndexedDB, and nothing in the new code reads
 * or clears these keys. Without this migration every user who was logged in when
 * this build ships keeps a valid refresh token in `localStorage` indefinitely,
 * readable by any XSS — the exact exposure the worker migration exists to remove.
 *
 * Runs at every boot rather than once: a user can arrive with a half-migrated
 * session (logged in on an old build in another tab), and the operation is cheap
 * and idempotent. Never throws — a storage failure must not block the app.
 *
 * @returns {string[]} the keys actually removed, for logging and tests.
 */
export default function clearLegacyTokens() {
    const removed = [];
    LEGACY_AUTH_KEYS.forEach(key => {
        try {
            if (localStorage.getItem(key) !== null) {
                localStorage.removeItem(key);
                removed.push(key);
            }
        } catch (error) {
            // Private browsing / blocked site data: nothing to clean up there.
            console.error(`[auth] could not clear the legacy "${key}" entry`, error);
        }
    });
    return removed;
}

export { LEGACY_AUTH_KEYS };
